import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { Grid, GridColumn, Gutter, useApi } from '@axiom/ui';
import type { Experience } from '@axiom/validation';

import { UserApi } from '../../api/user-api';
import { TaxonomyApi } from '../../api/taxonomy-api';
import { CandidateUtil } from '../../utils/candidate-util';
import { CandidateProfileIssuesApi } from '../../api/candidate-profile-issues-api';
import { CandidateExperienceApi } from '../../api/candidate-experience-api';
import { CandidateApi } from '../../api/candidate-api';

import {
  ExperienceEditSectionKeys,
  ExperienceEditUtil,
} from './experience-edit-util';
import type { ExperienceSectionStateType } from './ExperienceEditContext';
import { ExperienceEditContext } from './ExperienceEditContext';
import { ExperienceEditHeader } from './ExperienceEditHeader';
import { ExperienceEditTips } from './ExperienceEditTips';
import {
  ExperienceEditCompanyDetailsAxiom,
  CompanyDetailsAxiomSchema,
} from './ExperienceEditCompanyDetailsAxiom';
import {
  ExperienceEditCompanyDetailsNonAxiom,
  CompanyDetailsNonAxiomSchema,
} from './ExperienceEditCompanyDetailsNonAxiom';
import { ExperienceEditFormWorkDetails } from './ExperienceEditFormWorkDetails';
import { ExperienceEditFormFocusAreas } from './ExperienceEditFormFocusAreas';
import { ExperienceEditFormSkills } from './ExperienceEditFormSkills';

const { COMPANY_DETAILS, WORK_DETAILS, FOCUS_AREAS, SKILLS } =
  ExperienceEditSectionKeys;

export const ExperienceEdit = () => {
  const { experienceId, candidateId: urlCandidateId } = useParams();
  const [{ data: user }, { data: taxonomy }] = useApi(
    UserApi.readUser(),
    TaxonomyApi.readTaxonomy()
  );
  const candidateId = CandidateUtil.reconcileUserCandidateId(
    user,
    urlCandidateId
  );
  const [
    { data: candidate },
    { data: profileIssues },
    { data: experiences } = { data: [] },
  ] = useApi(
    CandidateApi.readCandidate(candidateId),
    CandidateProfileIssuesApi.readCandidateProfileIssues(candidateId),
    experienceId
      ? CandidateExperienceApi.readExperiences(candidateId)
      : undefined
  );
  const experience =
    experiences.find(exp => exp.id === experienceId) ??
    ({} as Partial<Experience>);

  const companyDetailsComplete = ExperienceEditUtil.isDataValid(
    experience.isAxiom
      ? CompanyDetailsAxiomSchema
      : CompanyDetailsNonAxiomSchema,
    experience
  );
  const workDetailsComplete = ExperienceEditUtil.isWorkDetailsComplete(
    experience,
    profileIssues.issues
  );
  const focusAreasComplete =
    ExperienceEditUtil.isFocusAreasComplete(experience);
  const skillsComplete = ExperienceEditUtil.isSkillsComplete(experience);

  const [accordionState, setAccordionState] =
    useState<ExperienceSectionStateType>({
      [COMPANY_DETAILS]: !companyDetailsComplete,
      [WORK_DETAILS]: companyDetailsComplete && !workDetailsComplete,
      [FOCUS_AREAS]:
        companyDetailsComplete && workDetailsComplete && !focusAreasComplete,
      [SKILLS]:
        companyDetailsComplete &&
        workDetailsComplete &&
        focusAreasComplete &&
        !skillsComplete,
    });

  const [dirtyFormState, setDirtyFormState] =
    useState<ExperienceSectionStateType>({
      [COMPANY_DETAILS]: false,
      [WORK_DETAILS]: false,
      [FOCUS_AREAS]: false,
      [SKILLS]: false,
    });

  const providerValue = useMemo(
    () => ({
      accordionState,
      setAccordionState,
      dirtyFormState,
      setDirtyFormState,
    }),
    [accordionState, dirtyFormState]
  );

  return (
    <div data-test="EXPERIENCE_EDIT">
      <ExperienceEditContext.Provider value={providerValue}>
        <ExperienceEditHeader
          name="EXPERIENCE_EDIT_HEADER"
          experience={experience}
        />
        <Gutter bottom="24px" />
        <Grid>
          <GridColumn smallScreenWidth={12} largeScreenWidth={8}>
            {experience.isAxiom ? (
              <ExperienceEditCompanyDetailsAxiom
                name="EXPERIENCE_EDIT_COMPANY_DETAILS_AXIOM"
                candidateId={candidateId}
                experience={experience as Experience}
                issues={profileIssues.issues}
                taxonomy={taxonomy}
              />
            ) : (
              <ExperienceEditCompanyDetailsNonAxiom
                name="EXPERIENCE_EDIT_COMPANY_DETAILS_NON_AXIOM"
                candidateId={candidateId}
                experience={experience}
                issues={profileIssues.issues}
                taxonomy={taxonomy}
              />
            )}
            <Gutter bottom="16px" />
            <ExperienceEditFormWorkDetails
              name="EXPERIENCE_EDIT_FORM_WORK_DETAILS"
              candidate={candidate}
              expandable={companyDetailsComplete}
              experience={experience as Experience}
              issues={profileIssues.issues}
            />
            <Gutter bottom="16px" />
            <ExperienceEditFormFocusAreas
              name="EXPERIENCE_EDIT_FORM_FOCUS_AREAS"
              candidate={candidate}
              expandable={!!companyDetailsComplete && workDetailsComplete}
              experience={experience as Experience}
              taxonomy={taxonomy}
            />
            <Gutter bottom="16px" />
            <ExperienceEditFormSkills
              name="EXPERIENCE_EDIT_FORM_SKILLS"
              candidate={candidate}
              expandable={
                companyDetailsComplete && workDetailsComplete
                  ? focusAreasComplete
                  : undefined
              }
              experience={experience as Experience}
              taxonomy={taxonomy}
            />
            <Gutter bottom="24px" />
          </GridColumn>
          <GridColumn smallScreenWidth={12} largeScreenWidth={4}>
            <ExperienceEditTips
              name="EXPERIENCE_EDIT_TIPS"
              experience={experience}
            />
          </GridColumn>
        </Grid>
      </ExperienceEditContext.Provider>
    </div>
  );
};
